import { Alert, Box, List, ListItemButton, ListItemText, ListSubheader, Paper, Typography } from '@mui/material';
import { useMemo, useRef, type MouseEvent } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';

import { useAuth } from '../../auth/useAuth.js';
import { AccessDeniedPage } from '../AccessDeniedPage.js';
import { AdminUsersPage } from '../admin/AdminUsersPage.js';
import { FeeConfigPage } from '../fees/FeeConfigPage.js';
import { OnboardingPolicyPage } from '../onboarding/OnboardingPolicyPage.js';

import { AlertRoutingPage } from './AlertRoutingPage.js';
import { CurrenciesOfferedPage } from './CurrenciesOfferedPage.js';
import { OrganisationSettingsPage } from './OrganisationSettingsPage.js';
import { RiskScoringModelPage } from './RiskScoringModelPage.js';
import { SystemSettingsPage } from './SystemSettingsPage.js';
import { TransactionMonitoringPage } from './TransactionMonitoringPage.js';
import { TreasuryAccountsPage } from './TreasuryAccountsPage.js';
import { TreasuryCommissionTransferPage } from './TreasuryCommissionTransferPage.js';
import {
  canReadSection,
  defaultSectionPath,
  findSection,
  groupSectionPath,
  legacySectionPath,
  sectionPath,
  settingsMenu,
} from './settings-sections.js';
import { LEAVE_WITH_UNSAVED, UnsavedChangesContext, type UnsavedRegistry } from './unsaved-changes.js';

/** The page for each section in settings-sections.ts, keyed `group/section`. */
const PAGES: Readonly<Record<string, () => JSX.Element>> = {
  'organisation/company': () => <OrganisationSettingsPage />,
  'organisation/currencies': () => <CurrenciesOfferedPage />,
  'access/admin-users': () => <AdminUsersPage />,
  'connections/providers': () => <SystemSettingsPage />,
  'treasury/accounts': () => <TreasuryAccountsPage />,
  'treasury/commission-transfer': () => <TreasuryCommissionTransferPage />,
  'pricing/fees': () => <FeeConfigPage />,
  'compliance/onboarding-policy': () => <OnboardingPolicyPage />,
  'compliance/transaction-monitoring': () => <TransactionMonitoringPage />,
  'compliance/risk-scoring': () => <RiskScoringModelPage />,
  'notifications/alerts': () => <AlertRoutingPage />,
};

/**
 * Settings, grouped by the area that owns each section (DEV-132). Served at
 * `/settings`, at the old one-segment `/settings/<tab>` addresses (redirected),
 * and at `/settings/<group>/<section>`.
 */
export function SettingsHubPage(): JSX.Element {
  const { group, section } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  // Which forms on the open section have unsaved changes (DEV-133); the menu
  // asks before leaving while any does.
  const unsaved = useRef(new Set<string>());
  const registry = useMemo<UnsavedRegistry>(() => ({
    set: (owner, dirty) => { if (dirty) unsaved.current.add(owner); else unsaved.current.delete(owner); },
  }), []);
  const role = user?.role;
  const home = defaultSectionPath(role);
  if (home === undefined) return <AccessDeniedPage />;

  // `/settings`, a group's own address, and the old `/settings/<tab>` addresses.
  if (group === undefined) {
    if (section === undefined) return <Navigate to={home} replace />;
    const moved = groupSectionPath(role, section) ?? legacySectionPath(section);
    return moved === undefined ? <AccessDeniedPage /> : <Navigate to={`${moved}${location.search}${location.hash}`} replace />;
  }

  const selected = findSection(group, section);
  if (selected === undefined || !canReadSection(role, selected)) return <AccessDeniedPage />;
  const render = PAGES[`${selected.group}/${selected.id}`];
  // A registry entry without a page is a build mistake, not a permission problem.
  if (render === undefined) return <Alert severity="error">{selected.label} has no page yet.</Alert>;

  return (
    <Box>
      <Typography variant="h4">System settings</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
        Configure the organisation, access, connections, money, pricing, compliance and notifications.
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '240px minmax(0, 1fr)' }, gap: 3, alignItems: 'start' }}>
        <Paper variant="outlined" component="nav" aria-label="Settings sections">
          <List dense disablePadding>
            {settingsMenu(role).map((menuGroup) => (
              <Box key={menuGroup.id} component="li" sx={{ listStyle: 'none' }}>
                <List dense disablePadding subheader={
                  <ListSubheader disableSticky sx={{ lineHeight: '32px', mt: 1, textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.6 }}>
                    {menuGroup.label}
                  </ListSubheader>
                }>
                  {menuGroup.sections.map((item) => {
                    const current = item.group === selected.group && item.id === selected.id;
                    return (
                      <ListItemButton
                        key={item.id}
                        component={Link}
                        to={sectionPath(item)}
                        selected={current}
                        aria-current={current ? 'page' : undefined}
                        onClick={(event: MouseEvent) => {
                          const newTab = event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0;
                          if (!current && !newTab && unsaved.current.size > 0 && !window.confirm(LEAVE_WITH_UNSAVED)) event.preventDefault();
                        }}
                        sx={{ borderRadius: 1, mx: 1, mb: 0.25 }}
                      >
                        <ListItemText primary={item.label} />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Box>
            ))}
          </List>
        </Paper>
        <Box role="region" aria-label={selected.label} sx={{ minWidth: 0 }}>
          <UnsavedChangesContext.Provider value={registry}>{render()}</UnsavedChangesContext.Provider>
        </Box>
      </Box>
    </Box>
  );
}
